import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { K } from './apiServiceI18n';
import ApiServiceOverview from './overview/ApiServiceOverview';
import ApiServicePerformanceDashboard from './performance/ApiServicePerformanceDashboard';
import ApiServiceCreationWizard from './ApiServiceCreationWizard';
import ApiServiceCancelWarningDialog from './ApiServiceCancelWarningDialog';
import useApiServicePermissions from './hooks/useApiServicePermissions';
import { loadWizardReturnContext, stripWizardReturnParam, clearWizardReturnContext } from './apiServiceWizardSession';

const TAB_OVERVIEW = 'overview';
const TAB_PERFORMANCE = 'performance';

// Screens that replace the tabbed overview view
const SCREEN_OVERVIEW = 'overview';
const SCREEN_WIZARD = 'wizard';
const tabsSx = {
  minHeight: 40,
  mb: 2,
  borderBottom: '1px solid #d5d9e0',
  '& .MuiTab-root': {
    minHeight: 40,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#5f6878',
    borderRadius: '6px 6px 0 0',
    mr: 0.5,
  },
  '& .Mui-selected': {
    color: '#fff !important',
    bgcolor: '#3d4654',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
};

export default function ApiServiceManagerPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
  const [performanceSeed, setPerformanceSeed] = useState(null);
  const [screen, setScreen] = useState(SCREEN_OVERVIEW);
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);
  const [pendingMainTab, setPendingMainTab] = useState(null);
  const [exitWarnOpen, setExitWarnOpen] = useState(false);
  const [wizardUnsavedList, setWizardUnsavedList] = useState([]);
  const wizardDialogApiRef = useRef(null);
  const { canModify } = useApiServicePermissions();

  const hasWizardUnsaved = wizardUnsavedList.length > 0;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('wizardReturn') === '1' && loadWizardReturnContext()) {
      setScreen(SCREEN_WIZARD);
    }
    stripWizardReturnParam();
  }, []);

  const navigateToMainTab = useCallback((tab) => {
    if (tab === TAB_PERFORMANCE) {
      setPerformanceSeed(null);
    }
    setActiveTab(tab);
    clearWizardReturnContext();
    setOverviewRefreshKey((k) => k + 1);
  }, []);

  const handleTabChange = (_e, value) => {
    if (value === TAB_PERFORMANCE) {
      setPerformanceSeed(null);
    }
    setActiveTab(value);
  };

  const onNavigatePerformance = useCallback((row) => {
    setPerformanceSeed(row);
    setActiveTab(TAB_PERFORMANCE);
  }, []);

  const onCreateApi = useCallback(() => {
    if (!canModify) return;
    setScreen(SCREEN_WIZARD);
  }, [canModify]);

  const handleWizardClose = useCallback(() => {
    clearWizardReturnContext();
    setScreen(SCREEN_OVERVIEW);
    setOverviewRefreshKey((k) => k + 1);
  }, []);

  const handleRequestMainTab = useCallback(
    (tab) => {
      if (hasWizardUnsaved) {
        setPendingMainTab(tab);
        setExitWarnOpen(true);
        return;
      }
      navigateToMainTab(tab);
    },
    [hasWizardUnsaved, navigateToMainTab]
  );

  const handleWizardCancelRequest = useCallback(() => {
    if (hasWizardUnsaved) {
      setPendingMainTab(null);
      setExitWarnOpen(true);
      return;
    }
    handleWizardClose();
  }, [hasWizardUnsaved, handleWizardClose]);

  const handleExitStayInWizard = useCallback(() => {
    setExitWarnOpen(false);
    setPendingMainTab(null);
    wizardDialogApiRef.current?.onSaveDialogClose?.();
  }, []);

  const handleExitDiscard = useCallback(() => {
    setExitWarnOpen(false);
    const tab = pendingMainTab;
    setPendingMainTab(null);
    wizardDialogApiRef.current?.discard?.();
    if (tab) {
      if (tab === TAB_PERFORMANCE) {
        setPerformanceSeed(null);
      }
      setActiveTab(tab);
    }
  }, [pendingMainTab]);

  const handleSeedConsumed = useCallback(() => {
    setPerformanceSeed(null);
  }, []);

  const pageTitle = t(K.API_SERVICE_MANAGER_WINDOW);

  const fullScreenBoxSx = {
    p: 2,
    minHeight: '100%',
    bgcolor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  };

  // ── Full-screen overlay screens (wizard / edit / view) ────────────────────
  if (screen === SCREEN_WIZARD) {
    return (
      <Box sx={fullScreenBoxSx}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, flexShrink: 0 }}>
          {pageTitle}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ApiServiceCreationWizard
            onClose={handleWizardClose}
            onCancelClick={handleWizardCancelRequest}
            onRequestMainTab={handleRequestMainTab}
            onUnsavedListChange={setWizardUnsavedList}
            registerDialogApi={(api) => {
              wizardDialogApiRef.current = api;
            }}
          />
        </Box>

        <ApiServiceCancelWarningDialog
          open={exitWarnOpen}
          onClose={handleExitStayInWizard}
          onSaveChanges={handleExitStayInWizard}
          onConfirmCancel={handleExitDiscard}
        />
      </Box>
    );
  }

  // ── Tabbed overview / performance ─────────────────────────────────────────
  return (
    <Box sx={{ p: 2, minHeight: '100%', bgcolor: '#fff' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        {pageTitle}
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={tabsSx}>
        <Tab label={t(K.API_SVC_TAB_OVERVIEW)} value={TAB_OVERVIEW} />
        <Tab label={t(K.API_SVC_TAB_PERFORMANCE)} value={TAB_PERFORMANCE} />
      </Tabs>

      {activeTab === TAB_OVERVIEW && (
        <ApiServiceOverview
          key={overviewRefreshKey}
          onNavigatePerformance={onNavigatePerformance}
          onCreateApi={onCreateApi}
        />
      )}

      {activeTab === TAB_PERFORMANCE && (
        <ApiServicePerformanceDashboard
          seedApi={performanceSeed}
          onSeedConsumed={handleSeedConsumed}
        />
      )}

    </Box>
  );
}
